import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
// constants
import { INITIAL_PER_PAGE } from 'constants/table'
// utils
import debounce from 'lodash/debounce'

type Filter = { [key: string]: any }

interface Context {
  page: number
  search: string
  filter: Filter
  perPage: number
  clearFilter: () => void
  handleSetPage: (value: number) => void
  handleSetFilter: (value: Filter) => void
  handleSetSearch: (eventOrValue?: React.ChangeEvent<HTMLInputElement> | string) => void
  handleSetPerPage: (value: number) => void
}

interface Props {
  children?: React.ReactNode
  initialFilter?: Filter
}

export const TableManagementContext = React.createContext<Context>({} as any)

const TableManagementProvider: React.FC<Props> = ({ children, initialFilter = {} }) => {
  const router = useRouter()

  const [search, setSearch] = React.useState<string>('')
  const [filter, setFilter] = React.useState<Filter>(initialFilter)
  const [page, setPage] = React.useState<number>(0)
  const [perPage, setPerPage] = React.useState<number>(INITIAL_PER_PAGE)

  const slug = router.query.slug?.[0]

  const debouncedSearch = React.useCallback(
    debounce((value: string) => {
      if (page > 0) {
        setPage(0)
      }
      setSearch(value)
    }, 500),
    [page]
  )

  const handleSetSearch = (eventOrValue: React.ChangeEvent<HTMLInputElement> | string) => {
    if (typeof eventOrValue === 'string') {
      return setSearch(eventOrValue)
    }

    debouncedSearch(eventOrValue.target.value)
  }

  const handleSetFilter = (value: Filter) => {
    setPage(0)
    setFilter((prev) => ({ ...prev, ...value }))
  }

  const clearFilter = () => {
    setFilter(null)
  }

  const handleSetPage = (value: number) => setPage(value)

  const handleSetPerPage = (value: number) => setPerPage(value)

  React.useEffect(() => {
    handleSetPage(0)
  }, [slug])

  const value = {
    page,
    search,
    filter,
    perPage,
    clearFilter,
    handleSetPage,
    handleSetFilter,
    handleSetSearch,
    handleSetPerPage
  }

  return <TableManagementContext.Provider value={value}>{children}</TableManagementContext.Provider>
}

export default TableManagementProvider
